import React, { useState } from "react";
import {
  MdAdd,
  MdChevronLeft,
  MdChevronRight,
  MdDelete,
  MdGridView,
  MdModeEditOutline,
  MdSearch,
  MdViewList,
} from "react-icons/md";
import rubriqueService from "../../../../service/rubriques.service";
import Swal from "sweetalert2";
import RubriqueModal from "./RubriqueModal";
import { motion } from "framer-motion";
import { usePhotoUrl } from "../../../../utils/usePhotoUrl";

const CardRubriques = ({ isOpen: openModalProp, rubriques, setRubriques }) => {
  const [isGridView, setIsGridView] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [editingRubrique, setEditingRubrique] = useState(null);
  const itemsPerPage = 4;
  const { getPhotoUrl } = usePhotoUrl();

  const filteredRubriques = rubriques.filter(
    (rubrique) =>
      rubrique.name_rub?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rubrique.description_rub?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculer l'index de début et de fin des rubriques à afficher
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRubriques.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredRubriques.length / itemsPerPage);

  const handleDelete = async (id) => {
    // Using SweetAlert2 for confirmation instead of window.confirm
    Swal.fire({
      title: "Êtes-vous sûr?",
      text: "Voulez-vous vraiment supprimer cette rubrique?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#57A863",
      cancelButtonColor: "#d33",
      confirmButtonText: "Oui, supprimer!",
      cancelButtonText: "Annuler",
      heightAuto: false,
      customClass: {
        container: "swal-container",
        popup: "rounded-xl border border-gray-200",
        title: "text-xl font-bold text-gray-800",
        htmlContainer: "text-base text-gray-600",
        confirmButton: "rounded-lg text-white font-medium",
        cancelButton: "rounded-lg font-medium",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Afficher l'animation de chargement
        Swal.fire({
          title: "Suppression en cours",
          html: `
            <div class="flex flex-col items-center">
              <div class="relative w-16 h-16">
                <div class="absolute top-0 left-0 w-16 h-16 border-4 border-red-500 border-opacity-20 rounded-full"></div>
                <div class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
              </div>
              <p class="mt-4 text-gray-700">Veuillez patienter...</p>
            </div>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        try {
          await rubriqueService.deleteRubrique(id);
          setRubriques(rubriques.filter((rubrique) => rubrique.id_rub !== id));

          // Show success message after deletion
          Swal.fire({
            icon: "success",
            title: "Supprimé!",
            text: "La rubrique a été supprimée avec succès.",
            confirmButtonColor: "#57A863",
            customClass: {
              popup: "rounded-xl border border-gray-200",
              title: "text-xl font-bold text-gray-800",
              htmlContainer: "text-base text-gray-600",
              confirmButton: "rounded-lg text-white font-medium",
            },
          });
        } catch (error) {
          // Show error message if deletion fails
          Swal.fire({
            icon: "error",
            title: "Erreur!",
            text: "La suppression a échoué.",
            confirmButtonColor: "#d33",
            customClass: {
              popup: "rounded-xl border border-gray-200",
              title: "text-xl font-bold text-red-600",
              htmlContainer: "text-base text-gray-600",
              confirmButton: "rounded-lg text-white font-medium",
            },
          });
          setError(error);
        }
      }
    });
  };

  const openModal = (rubrique = null) => {
    setEditingRubrique(rubrique);
    setModalError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRubrique(null);
    setModalError(null);
  };

  const handleModalSubmit = async (formData) => {
    setModalLoading(true);
    setModalError(null);

    try {
      console.log(
        "📤 Données reçues au Rubrique:",
        Array.from(formData.entries()).reduce((obj, [key, value]) => {
          obj[key] = value instanceof File ? `File: ${value.name}` : value;
          return obj;
        }, {})
      );

      // Vérifier que les données sont présentes dans le FormData
      if (!formData.get("name_rub") || !formData.get("description_rub")) {
        throw new Error("Des champs requis sont manquants");
      }

      let response;
      if (editingRubrique) {
        // Ajout de la méthode PUT pour l'édition
        formData.append("_method", "PUT");
        response = await rubriqueService.updateRubrique(
          editingRubrique.id_rub,
          formData
        );

        // Mettre à jour la liste des rubriques
        setRubriques(
          rubriques.map((r) =>
            r.id_rub === editingRubrique.id_rub ? { ...r, ...response.data } : r
          )
        );
      } else {
        // Création d'une nouvelle rubrique
        response = await rubriqueService.createRubrique(formData);

        // Ajouter la nouvelle rubrique à la liste
        setRubriques([...rubriques, response.data]);
      }

      closeModal();
      return Promise.resolve(response);
    } catch (err) {
      console.error("Erreur lors de la soumission:", err);

      // Extraire des messages d'erreur plus détaillés
      let errorMessage = "Une erreur est survenue";

      if (err.response?.data?.message) {
        // Format Laravel/API habituel
        if (typeof err.response.data.message === "object") {
          // Extraction des messages d'erreur de validation
          errorMessage = Object.values(err.response.data.message)
            .flat()
            .join("\n");
        } else {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setModalError(errorMessage);
      return Promise.reject(err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    openModal();
  };

  if (error) return <div className="text-red-500">Erreur : {error}</div>;

  return (
    <div className="w-full rounded-lg bg-gray-50 p-6 shadow-sm">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-800">
          {filteredRubriques.length} Rubriques disponibles
        </h1>
        <button
          className="flex items-center justify-center rounded-md bg-[#57A863] px-6 py-2.5 text-white transition-all hover:bg-green-800 hover:shadow-lg"
          onClick={handleOpenAddModal}
        >
          <MdAdd className="mr-2 h-5 w-5" />
          Ajouter une rubrique
        </button>
      </div>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-md flex-1">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une rubrique..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="focus:border-transparent w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 transition-all focus:ring-2 focus:ring-[#57A863]"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsGridView(false)}
            className={`rounded-lg p-2.5 transition-all ${
              !isGridView
                ? "bg-[#57A863] text-white shadow-md"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            <MdViewList className="text-xl" />
          </button>
          <button
            onClick={() => setIsGridView(true)}
            className={`rounded-lg p-2.5 transition-all ${
              isGridView
                ? "bg-[#57A863] text-white shadow-md"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            <MdGridView className="text-xl" />
          </button>
        </div>
      </div>

      <div
        className={`${
          isGridView
            ? "grid grid-cols-1 gap-6 md:grid-cols-2"
            : "flex flex-col gap-4"
        }`}
      >
        {currentItems.length > 0 ? (
          currentItems.map((rubrique) => (
            <motion.div
              key={rubrique.id_rub}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="rounded-xl bg-white p-6 shadow-sm transition-all hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 overflow-hidden rounded-full border-2 border-gray-200">
                    <img
                      src={getPhotoUrl(rubrique.photo_rub)}
                      alt={rubrique.name_rub}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {rubrique.name_rub}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {rubrique.description_rub}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openModal(rubrique)}
                    className="rounded-lg bg-gray-100 p-2 text-gray-600 transition-colors hover:bg-gray-200"
                    title="Modifier"
                  >
                    <MdModeEditOutline className="text-xl" />
                  </button>
                  <button
                    onClick={() => handleDelete(rubrique.id_rub)}
                    className="rounded-lg bg-red-100 p-2 text-red-500 transition-colors hover:bg-red-200"
                    title="Supprimer"
                  >
                    <MdDelete className="text-xl" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-16 text-center">
            <svg
              className="mb-3 h-16 w-16 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <p className="mb-1 text-lg font-medium text-gray-500">
              Aucune rubrique trouvée
            </p>
            <p className="text-sm text-gray-400">
              {searchTerm
                ? "Essayez de modifier votre recherche"
                : "Ajoutez une nouvelle rubrique pour commencer"}
            </p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2.5 text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <MdChevronLeft className="text-xl" />
          </button>
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`h-10 min-w-[2.5rem] rounded-lg ${
                  currentPage === page
                    ? "bg-[#57A863] text-white"
                    : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                } transition-all`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2.5 text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <MdChevronRight className="text-xl" />
          </button>
        </div>
      )}

      {/* Modal amélioré pour ajout/modification */}
      <RubriqueModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        rubrique={editingRubrique}
        isLoading={modalLoading}
        error={modalError}
      />
    </div>
  );
};

export default CardRubriques;
